import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { logger } from "@/utils/logger";

export type SocialConnection = Tables<"social_connections">;
export type SocialConnectionInsert = TablesInsert<"social_connections">;
export type SocialConnectionUpdate = TablesUpdate<"social_connections">;

// Type for connected platforms without sensitive tokens
export type ConnectedPlatform = Omit<SocialConnection, 'access_token' | 'refresh_token'>;

export interface MetricoolConnectionStatus {
  platform: string;
  connected: boolean;
  username?: string;
  picture?: string;
  providerUserId?: string;
}

export const getConnectedPlatforms = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("social_connections")
      .select("id, user_id, platform, connected, username, platform_user_id, is_active, expires_at, created_at, updated_at")
      .eq("user_id", userId)
      .eq("connected", true);

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error fetching connected platforms:", error);
    return { success: false, error, data: [] };
  }
};

export const getAllPlatforms = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("social_connections")
      .select("id, user_id, platform, connected, username, platform_user_id, is_active, expires_at, created_at, updated_at")
      .eq("user_id", userId)
      .order("platform");

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error fetching platforms:", error);
    return { success: false, error, data: [] };
  }
};

export const updateConnectionStatus = async (
  id: string,
  updates: SocialConnectionUpdate
) => {
  try {
    const { data, error } = await supabase
      .from("social_connections")
      .update(updates)
      .eq("id", id)
      .select("id, user_id, platform, connected, username, platform_user_id, is_active, expires_at, created_at, updated_at")
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating connection status:", error);
    return { success: false, error };
  }
};

export const initializeUserPlatforms = async (userId: string) => {
  try {
    // Vérifier si l'utilisateur a déjà des connexions
    const { data: existing } = await supabase
      .from("social_connections")
      .select("id, user_id, platform, connected, username, platform_user_id, is_active, expires_at, created_at, updated_at")
      .eq("user_id", userId);

    if (existing && existing.length > 0) {
      return { success: true, data: existing };
    }

    // Créer les 5 plateformes par défaut
    const platforms: SocialConnectionInsert[] = [
      { user_id: userId, platform: "linkedin", connected: false },
      { user_id: userId, platform: "instagram", connected: false },
      { user_id: userId, platform: "tiktok", connected: false },
      { user_id: userId, platform: "youtube", connected: false },
      { user_id: userId, platform: "facebook", connected: false },
    ];

    const { data, error } = await supabase
      .from("social_connections")
      .insert(platforms)
      .select("id, user_id, platform, connected, username, platform_user_id, is_active, expires_at, created_at, updated_at");

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error initializing platforms:", error);
    return { success: false, error };
  }
};

/**
 * Fetch connection statuses from Metricool API via edge function
 */
export const fetchMetricoolConnections = async (userId: string) => {
  try {
    // If the user is not authenticated, Supabase will send the anon JWT as Authorization,
    // which has no `sub` claim and will be rejected by the edge function.
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.warn("[fetchMetricoolConnections] Session error:", sessionError.message);
      return { success: false, error: "UNAUTHENTICATED", connections: [] as MetricoolConnectionStatus[] };
    }

    const accessToken = sessionData.session?.access_token;

    if (!accessToken) {
      // Expected state when logged out / session expired: don't call the edge function.
      console.log("[fetchMetricoolConnections] No access token, skipping fetch");
      return { success: false, error: "UNAUTHENTICATED", connections: [] as MetricoolConnectionStatus[] };
    }

    console.log("[fetchMetricoolConnections] Fetching for user:", userId);

    const { data, error } = await supabase.functions.invoke("get-metricool-connections", {
      body: { user_id: userId },
    });

    if (error) {
      // Avoid noisy Sentry/blank-screen reports for auth-expired cases.
      const msg = error.message || String(error) || "Unknown error";
      const isAuthError = msg.includes("401") || 
                          msg.toLowerCase().includes("invalid token") || 
                          msg.toLowerCase().includes("unauthorized") ||
                          msg.toLowerCase().includes("jwt");
      
      if (isAuthError) {
        console.log("[fetchMetricoolConnections] Auth error, returning empty:", msg);
        return { success: false, error: "UNAUTHENTICATED", connections: [] as MetricoolConnectionStatus[] };
      }

      console.error("[fetchMetricoolConnections] Edge function error:", error);
      return { success: false, error: msg, connections: [] as MetricoolConnectionStatus[] };
    }

    if (!data?.success) {
      const apiError = data?.error || "Unknown error";
      const apiErrorStr = typeof apiError === "string" ? apiError : JSON.stringify(apiError);
      const isAuthError = apiErrorStr.toLowerCase().includes("token") || 
                          apiErrorStr.toLowerCase().includes("unauthorized") ||
                          apiErrorStr.toLowerCase().includes("jwt");
      
      if (isAuthError) {
        console.log("[fetchMetricoolConnections] API auth error, returning empty:", apiErrorStr);
        return { success: false, error: "UNAUTHENTICATED", connections: [] as MetricoolConnectionStatus[] };
      }

      console.error("[fetchMetricoolConnections] API error:", apiError);
      return { success: false, error: apiErrorStr, connections: [] as MetricoolConnectionStatus[] };
    }

    console.log("[fetchMetricoolConnections] Got connections:", data.connections?.length);
    return {
      success: true,
      connections: data.connections as MetricoolConnectionStatus[],
      blogId: data.blogId,
    };
  } catch (error) {
    // Catch any unhandled errors and prevent blank screens
    const errorMsg = error instanceof Error ? error.message : String(error);
    const isAuthError = errorMsg.toLowerCase().includes("401") ||
                        errorMsg.toLowerCase().includes("token") ||
                        errorMsg.toLowerCase().includes("unauthorized");
    
    if (isAuthError) {
      console.log("[fetchMetricoolConnections] Caught auth error:", errorMsg);
      return { success: false, error: "UNAUTHENTICATED", connections: [] as MetricoolConnectionStatus[] };
    }
    
    console.error("[fetchMetricoolConnections] Error:", error);
    return {
      success: false,
      error: errorMsg,
      connections: [] as MetricoolConnectionStatus[],
    };
  }
};

export interface ConnectionLinkOptions {
  menu_id?: string;
  input_id?: string;
  email?: string;
}

type ConnectionLinkResult = {
  success: boolean;
  link?: string;
  error?: string;
  reason?: 'token_expired' | 'unknown';
};

const isExpectedTokenExpiredError = (msg: string) => {
  const m = msg.toLowerCase();
  return (
    m.includes('failed to generate link') ||
    m.includes("could not find 'connexions' tab") ||
    m.includes("connexions") ||
    m.includes('invalid token') ||
    m.includes('unauthorized') ||
    m.includes('jwt') ||
    m.includes('login')
  );
};

const TOKEN_EXPIRED_RESULT: ConnectionLinkResult = {
  success: false,
  reason: 'token_expired',
  error: 'TOKEN_EXPIRED'
};

/**
 * Creates an error result, returning token expired result if applicable
 */
const createErrorResult = (msg: string, logContext?: string): ConnectionLinkResult => {
  if (isExpectedTokenExpiredError(msg)) {
    if (logContext) {
      console.log(`[getConnectionLink] Expected token-expired outcome${logContext}`);
    }
    return TOKEN_EXPIRED_RESULT;
  }
  return { success: false, reason: 'unknown', error: msg };
};

/**
 * Handles edge function errors
 */
const handleEdgeFunctionError = (error: { message?: string }): ConnectionLinkResult => {
  const msg = error?.message || JSON.stringify(error) || 'Unknown error';
  const result = createErrorResult(msg, '');
  if (result.reason === 'unknown') {
    console.error('[getConnectionLink] Edge function error:', error);
  }
  return result;
};

/**
 * Extracts error message from data payload
 */
const extractPayloadErrorMessage = (err: unknown): string => {
  if (typeof err === 'string') return err;
  return (err as { message?: string })?.message ?? 'Unknown error';
};

/**
 * Processes successful edge function response data
 */
const processResponseData = (data: {
  reason?: string;
  message?: string;
  error?: unknown;
  detail?: unknown;
  link?: string;
}): ConnectionLinkResult => {
  logger.info('[getConnectionLink] processResponseData', {
    reason: data?.reason,
    hasLink: typeof data?.link === 'string',
    hasError: !!(data?.error ?? data?.detail),
  });

  if (data?.reason === 'token_expired') {
    logger.warn('[getConnectionLink] token_expired reason from edge function');
    return TOKEN_EXPIRED_RESULT;
  }

  if (data?.reason === 'network_error') {
    logger.warn('[getConnectionLink] network_error from edge function:', data?.message);
    return { success: false, reason: 'unknown', error: data?.message || 'Network error' };
  }

  const err = data?.error ?? data?.detail;
  if (err) {
    const msg = extractPayloadErrorMessage(err);
    return createErrorResult(msg, ' (payload)');
  }

  if (typeof data?.link === 'string') {
    logger.info('[getConnectionLink] Got link successfully');
    return { success: true, link: data.link };
  }

  logger.warn('[getConnectionLink] No link and no known error in response:', data);
  return { success: false, reason: 'unknown', error: 'No link in response' };
};

/**
 * Get social networks connection link via Metribad (Playwright) edge function.
 * Opens Metricool connections for the given blog and returns the connection link.
 */
export const getConnectionLink = async (
  userId: string,
  blogId: string,
  options?: ConnectionLinkOptions
): Promise<ConnectionLinkResult> => {
  logger.info('[getConnectionLink] Invoking edge function', { userId, blogId, hasEmail: !!options?.email });
  try {
    const { data, error } = await supabase.functions.invoke('generate-social-networks-connection-link', {
      body: {
        user_id: userId,
        blog_id: blogId,
        ...(options?.menu_id && { menu_id: options.menu_id }),
        ...(options?.input_id && { input_id: options.input_id }),
        ...(options?.email && { email: options.email }),
      },
    });

    logger.info('[getConnectionLink] Edge function responded', { hasData: !!data, hasError: !!error });

    if (error) {
      logger.error('[getConnectionLink] Edge function invocation error:', error);
      return handleEdgeFunctionError(error);
    }

    return processResponseData(data);
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Unknown error';
    const result = createErrorResult(msg, ' (catch)');
    if (result.reason === 'unknown') {
      console.error('[getConnectionLink] Error:', error);
    }
    return result;
  }
};

/**
 * Helper function to update an existing connection in the database
 */
const updateExistingConnection = async (
  local: ConnectedPlatform,
  mc: MetricoolConnectionStatus
): Promise<boolean> => {
  const needsUpdate = local.connected !== mc.connected || local.username !== mc.username;
  if (!needsUpdate) {
    return false;
  }

  const { error } = await supabase
    .from("social_connections")
    .update({
      connected: mc.connected,
      username: mc.username || local.username,
      platform_user_id: mc.providerUserId || local.platform_user_id,
      updated_at: new Date().toISOString(),
    })
    .eq("id", local.id);

  return !error;
};

/**
 * Helper function to insert a new connection in the database
 */
const insertNewConnection = async (
  userId: string,
  platform: string,
  mc: MetricoolConnectionStatus
): Promise<boolean> => {
  const { error } = await supabase
    .from("social_connections")
    .insert({
      user_id: userId,
      platform,
      connected: mc.connected,
      username: mc.username,
      platform_user_id: mc.providerUserId,
    });

  return !error;
};

/**
 * Helper function to process a single Metricool connection
 */
const processSingleConnection = async (
  userId: string,
  mc: MetricoolConnectionStatus,
  localMap: Map<string, ConnectedPlatform>
): Promise<boolean> => {
  const platform = mc.platform.toLowerCase();
  const local = localMap.get(platform);

  if (local) {
    return updateExistingConnection(local, mc);
  }
  return insertNewConnection(userId, platform, mc);
};

/**
 * Sync Metricool connection statuses to local social_connections table
 */
export const syncMetricoolConnectionsToDb = async (userId: string) => {
  try {
    console.log("[syncMetricoolConnectionsToDb] Starting sync for user:", userId);

    // 1. Fetch current status from Metricool
    const metricoolResult = await fetchMetricoolConnections(userId);
    if (!metricoolResult.success || metricoolResult.connections.length === 0) {
      console.log("[syncMetricoolConnectionsToDb] No Metricool data, skipping sync");
      return { success: true, synced: 0 };
    }

    // 2. Get existing local connections
    const { data: localConnections } = await supabase
      .from("social_connections")
      .select("id, user_id, platform, connected, username, platform_user_id, is_active, expires_at, created_at, updated_at")
      .eq("user_id", userId);

    const localMap = new Map(
      (localConnections || []).map(c => [c.platform.toLowerCase(), c])
    );

    // 3. For each Metricool connection, upsert to local DB
    const syncResults = await Promise.all(
      metricoolResult.connections.map(mc => processSingleConnection(userId, mc, localMap))
    );

    const synced = syncResults.filter(Boolean).length;

    console.log("[syncMetricoolConnectionsToDb] Synced", synced, "connections");
    return { success: true, synced };
  } catch (error) {
    console.error("[syncMetricoolConnectionsToDb] Error:", error);
    return { success: false, error };
  }
};
